import {
  Component,
  ChangeDetectionStrategy,
  HostBinding,
  Input,
  ElementRef,
  Renderer2,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasButton } from './gas-button.namespace';
import { GasIcon } from '../../utils/gas-icons/icon/gas-icon.namespace';
import { GasColor } from '../../core/colors/color.namespace';
import { GasSize } from '../../core/sizes/gas-size.enum';

/**
 * @description Кнопка, используется по селектору через аттрибут
 *
 * @example <button gasButton>test</button>
 * @example <button gasButton="brand">test</div>
 */
@Component({
  // eslint-disable-next-line
  selector: 'button[gasButton], div[gasButton], a[gasButton]',
  templateUrl: './gas-button.component.html',
  styleUrls: ['./gas-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasButtonComponent implements AfterViewInit, OnChanges {
  @HostBinding('class._disabled')
  @GasInputBoolean()
  @Input()
  public disabled: string | boolean = false;

  @HostBinding('class._loading')
  @GasInputBoolean()
  @Input()
  public loading: string | boolean = false;

  @HostBinding('attr.disabled')
  public get disabledAttr(): string {
    return this.disabled ? 'disabled' : null;
  };

  @HostBinding('attr.type')
  @Input('attrType')
  public attrType: ( 'button' | 'submit' | 'reset' ) = 'button';

  @Input('gasButton')
  public set type(type: GasButton.ButtonInputType) {
    this.innerType = type;
  }

  public get type(): GasButton.ButtonInputType {
    return this.innerType;
  }

  @HostBinding('class._primary')
  public get primaryClass(): boolean {
    return !this.type || this.type === GasButton.ButtonType.Primary;
  }

  @HostBinding('class._secondary')
  public get secondaryClass(): boolean {
    return this.type === GasButton.ButtonType.Secondary;
  }

  @HostBinding('class._negative')
  public get negativeClass(): boolean {
    return this.type === GasButton.ButtonType.Negative;
  }

  /** Имя иконки и ее добавление слева от текста */
  @Input()
  public suffixIconName: string | GasIcon.Icons;

  /** Имя иконки и ее добавление справа от текста */
  @Input()
  public postfixIconName: string | GasIcon.Icons;

  /** @deprecated */
  @Input()
  public set variant(variant: GasButton.ButtonType) {
    this.renderer.addClass(this.elementRef.nativeElement, `_${variant || 'brand'}`);
  }

  /** @deprecated */
  @Input()
  public set iconName(iconName: string | GasIcon.Icons) {
    this.suffixIconName = this.postfixIconName = iconName;
  }

  /** @deprecated */
  @Input()
  public set iconPosition(iconPosition: 'left' | 'right') {
    if (iconPosition === 'left') {
      this.suffixIconName = '';
    } else {
      this.postfixIconName = '';
    }
  }

  /** Размер иконки */
  @Input()
  public set iconSize(size: GasSize) {
    this.innerSize = size;
  }

  public get iconSize(): GasSize {
    return this.innerSize;
  }

  @HostBinding('class._not-default-size')
  public get isAnotherSize(): boolean {
    return this.innerSize !== GasSize.MdSize;
  }

  @ViewChild('content', { static: true })
  public contentRef: ElementRef;

  public get iconColor(): GasColor.Color {
    switch (this.type) {
      case GasButton.ButtonType.Primary:
        return GasColor.Color.White;

      case GasButton.ButtonType.Negative:
        return GasColor.Color.White;

      case GasButton.ButtonType.Secondary:
        return GasColor.Color.Brand;

      default:
        return GasColor.Color.White;
    }
  }

  private innerType: GasButton.ButtonInputType;
  private innerSize = GasSize.MdSize;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  public ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.type?.currentValue) {
      this.changeDetectorRef.detectChanges();
    }
  }

  public hasContent(): boolean {
    const element: HTMLElement = this.contentRef.nativeElement;

    return Boolean(element.textContent.trim());
  }
}
